import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Stack,
  Skeleton,
  Alert,
  Tooltip,
  Fade,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckIcon from '@mui/icons-material/Check';

const SWATCHES = [
  '#26619C', '#1A4A7A', '#5B8BC6', '#FFD60A',
  '#10B981', '#EF4444', '#F59E0B', '#3B82F6',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
  '#6366F1', '#84CC16', '#64748B', '#1E293B',
];

const CATEGORIES_QUERY = gql`
  query Categories {
    categories {
      id
      name
      color
    }
  }
`;

const CREATE_CATEGORY = gql`
  mutation CreateCategory($name: String!, $color: String) {
    createCategory(name: $name, color: $color) {
      category { id name color }
    }
  }
`;

const UPDATE_CATEGORY = gql`
  mutation UpdateCategory($id: Int!, $name: String, $color: String) {
    updateCategory(id: $id, name: $name, color: $color) {
      category { id name color }
    }
  }
`;

const DELETE_CATEGORY = gql`
  mutation DeleteCategory($id: Int!) {
    deleteCategory(id: $id) { ok }
  }
`;

function CategoryModal({ open, onClose, onSave, initial, loading, error }) {
  const [name, setName] = useState(initial?.name || '');
  const [color, setColor] = useState(initial?.color || SWATCHES[0]);

  React.useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setColor(initial?.color || SWATCHES[0]);
    }
  }, [open, initial]);

  const isEdit = Boolean(initial?.id);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {isEdit ? 'Editar categoría' : 'Nueva categoría'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 100 }}
          />

          {/* Color preview */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: color,
                border: '2px solid',
                borderColor: 'divider',
                flexShrink: 0,
                transition: 'background-color 0.2s ease',
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {color}
            </Typography>
          </Box>

          {/* Swatch grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 0.75,
            }}
          >
            {SWATCHES.map((swatch) => (
              <Tooltip key={swatch} title={swatch} arrow>
                <Box
                  onClick={() => setColor(swatch)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: swatch,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: color === swatch ? '2px solid' : '2px solid transparent',
                    borderColor: color === swatch ? 'text.primary' : 'transparent',
                    outline: color === swatch ? '2px solid white' : 'none',
                    outlineOffset: '-3px',
                    transition: 'transform 0.15s ease, outline 0.15s ease',
                    '&:hover': { transform: 'scale(1.2)' },
                  }}
                >
                  {color === swatch && (
                    <CheckIcon sx={{ fontSize: 14, color: '#fff', filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.6))' }} />
                  )}
                </Box>
              </Tooltip>
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={() => onSave({ name, color })}
          variant="contained"
          disabled={!name.trim() || loading}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Categories() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [mutationError, setMutationError] = useState('');

  const { data, loading, refetch } = useQuery(CATEGORIES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const [createCategory, { loading: creating }] = useMutation(CREATE_CATEGORY);
  const [updateCategory, { loading: updating }] = useMutation(UPDATE_CATEGORY);
  const [deleteCategory] = useMutation(DELETE_CATEGORY);

  const categories = data?.categories || [];

  const openCreate = () => {
    setEditTarget(null);
    setMutationError('');
    setModalOpen(true);
  };

  const openEdit = (cat) => {
    setEditTarget(cat);
    setMutationError('');
    setModalOpen(true);
  };

  const handleSave = async ({ name, color }) => {
    setMutationError('');
    try {
      if (editTarget) {
        await updateCategory({ variables: { id: parseInt(editTarget.id), name, color } });
      } else {
        await createCategory({ variables: { name, color } });
      }
      await refetch();
      setModalOpen(false);
    } catch (e) {
      setMutationError(e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCategory({ variables: { id: parseInt(id) } });
      await refetch();
    } catch (e) {
      // silent — could show a snackbar here
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <LocalOfferIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Categorías</Typography>
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ color: 'primary.dark', fontWeight: 700, alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          Nueva categoría
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ overflowX: 'auto' }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: '#EDF0F3',
                '& .MuiTableCell-head': {
                  color: 'text.primary',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  py: 1.5,
                },
              }}
            >
              <TableCell width="60px" align="center">Color</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell width="120px" align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && categories.length === 0
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell align="center"><Skeleton variant="circular" width={24} height={24} sx={{ mx: 'auto' }} /></TableCell>
                    <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                    <TableCell><Skeleton variant="text" /></TableCell>
                  </TableRow>
                ))
              : categories.map((cat) => (
                  <TableRow
                    key={cat.id}
                  >
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          backgroundColor: cat.color,
                          mx: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.4,
                            borderRadius: 1,
                            backgroundColor: `${cat.color}22`,
                            border: '1px solid',
                            borderColor: `${cat.color}55`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: cat.color }}
                          >
                            {cat.name}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center" spacing={0.5}>
                        <Tooltip title="Editar" arrow>
                          <IconButton size="small" onClick={() => openEdit(cat)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar" arrow>
                          <IconButton size="small" onClick={() => handleDelete(cat.id)} color="error">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

            {!loading && categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                  <LocalOfferIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    No hay categorías. ¡Crea la primera!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initial={editTarget}
        loading={creating || updating}
        error={mutationError}
      />
    </Container>
  );
}
